import React from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql } from 'gatsby'
import 'src/styles/sidebar.css';
import * as styles from 'src/uglib_styles/sidebarMenu.module.css';
import { contentExists } from 'src/utils/ug-utils';

/**
 * Render a menu to JSX unordered list.
 * @param {Object} menu - The top level menu item that we want to render 
 * @param {string[]} path_ids - A list of menu link ids that compose the active trail. This is used to check if a menu link is in the active path. 
 * @returns 
 */
function buildMenu(menu, path_ids) {
    const items = menu.children.map((child) => buildMenuItem(child, path_ids));

    if (menu.parent === null) {
        return (<>
            <h2>{ menu.title }</h2>
            <ul>{ items }</ul>
        </>);
    }
    else {
        return <ul>{ items }</ul>;
    }
}

/**
 * Render a menu item, which may or may not have children that need to be rendered as a menu in its own right. 
 * Only renders child items if they're relevant to our active path (the ID is included in the path_ids array)
 * @param {Object} menu_item 
 * @param {string[]} path_ids 
 * @returns 
 */
function buildMenuItem(menu_item, path_ids) {
    let path = menu_item.url;

    // If we're in the active menu, render the item and it's potential children.
    if (path_ids.includes(menu_item.id)) {
        let children = []
        if(contentExists(menu_item.children)) {
            children = buildMenu(menu_item, path_ids);
        }

        // Only add the current class to the actual page we are on. 
        let link_classes = '';
        if (path_ids[path_ids.length - 1] === menu_item.id) {
            link_classes = 'current ' + styles.current;
        }

        return (
            <li key={menu_item.id} className="active-path">
                <a className={link_classes} href={path}>{menu_item.title}</a>
                {children}
            </li>
        );
    }
    // If we're not in the active path, just render one menu item, no children.
    else {
        return (
            <li key={menu_item.id}>
                <a href={path}>{menu_item.title}</a>
            </li>
        );							
    }
}

/**
 * Trim off trailing slashes, and just in case, make sure that the path is relative.
 * @param {string} path A string representing a path. E.g.: /find/books
 * @returns A clean string that matches what shows up the menu query data. 
 */
function cleanPath(path) {
    if (path.endsWith('/')) {
        path = path.slice(0, path.length - 1);
    }

    if (!path.startsWith('/')) {
        path = path.replace(/http.*\//, '/');
    }

    return path;
}

/**
 * Search for a page in all of the available menu data. 
 * If you find it, return the name of the menu that it belongs to. 
 * @param {Object} menu_data - allMenuItems query results.
 * @param {string} path - The path of the page we're trying to find.
 * @returns A sting containing the name of the active menu, or null if we didn't find the page. 
 */
function getActiveMenu(menu_data, path) {
    const current_page = getCurrentMenuLinkByPath(menu_data, path);

    if (current_page) {
        return current_page.menu_name;
    }

    return null;
}

/**
 *  Given a path, find the corresponding menu entry in the query results.
 * 
 * @param {Object} menu_data 
 * @param {string} path The path of the page that we're looking for.
 * @returns An object representing a menu link or null if we can't find it.
 */
function getCurrentMenuLinkByPath(menu_data, path) {
    const clean_path = cleanPath(path);
    const results = menu_data.filter((item) => (item.url === clean_path));

    // Note: There's potential for some confusion here if there are multiple menu links with the same path.
    if (results.length > 0) {
        return results.shift();
    }

    return null;
}

/**
 *  Given a menu link ID, find the corresponding menu entry in the query results.
 * 
 * @param {Object} menu_data 
 * @param {string} id The id of the page that we're looking for. E.g.: menu-items-menu_link_content:05b64092-2b77-4fd4-8e5d-adc592ca3bdc
 * @returns An object representing a menu link or null if we can't find it.
 */
function getMenuLinkById(menu_data, id) {  
    const results = menu_data.filter((item) => (item.id === id));

    // Note: There's potential for some confusion here if there are multiple menu links with the same path.
    if (results.length > 0) {
        return results.shift();
    }

    return null;
}

/**
 * Recursive funciton to build the active trail of menu data. Basically what you would expect in a breadcrumb trail. 
 * We use this to check whether a menu item we're building is in the "active" path.
 * @param {Object} menu_data The full menu data query results for searching.
 * @param {Object} current The menu data representing the current page we are on.
 * @param {*} trail The trail data as it's being built. You don't have to pass this. 
 * @returns 
 */
function getMenuTrail(menu_data, current, trail = []) {
    trail.push(current);
    if (current.parent) {
        const parent = getMenuLinkById(menu_data, current.parent.id);
        getMenuTrail(menu_data, parent, trail);
    }
    else {
        trail.reverse();
    }

    return trail;
}

/**
 * 
 * @param {array[Object]} trail An array of menu data representing the active page trail.
 * @returns An array of menu item IDs representing the active trail
 */
function getMenuTrailIDs(trail) {
    return trail.map((item) => (item.id));
}

function SidebarMenu (props) {
    // NOTE: This query limits our menu structures to a max of 6 levels of depth. 
    const menu_data = useStaticQuery(graphql`
        query SidebarMenuQuery {
            allMenuItems {
                nodes {
                  id
                  title
                  url
                  weight
                  menu_name
                  children {
                    ... on MenuItems {
                      id
                      title
                      url
                      weight
                      children {
                        ... on MenuItems {
                          id
                          menu_name
                          title
                          url
                          weight
                          children {
                            ... on MenuItems {
                              id
                              menu_name
                              title
                              url
                              weight
                              children {
                                ... on MenuItems {
                                  id
                                  menu_name
                                  title
                                  url
                                  weight
                                  children {
                                    ... on MenuItems {
                                      id
                                      menu_name
                                      title
                                      url
                                      weight
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  parent {
                    ... on MenuItems {
                      id
                      children {
                        ... on MenuItems {
                          id
                          title
                          url
                          menu_name
                          weight
                        }
                      }
                    }
                  }
                }
            }
        }
	`).allMenuItems.nodes;

    /**
     * What menus should we search? 
     * We only render certian menus in the sidebar. The list of these menu names is passed in props.menu.
     */
    const active_menu = getActiveMenu(menu_data, props.url);

    // This shouldn't ever really happen, but just in case we can't find the page in the menu data we return an emtpy JSX element. 
    if(active_menu === null) {
        return <></>;
    }

    // To make searching a little easier, filter our dataset down to only the active menu.
    const active_menu_data = menu_data.filter((item) => (item.menu_name === active_menu));

    // Find our current page in the menu data.
    const current_menu_item = getCurrentMenuLinkByPath(active_menu_data, props.url);

    // Get a list of menu items in our active trail (like a breadcrumb trail). 
    const trail = getMenuTrail(active_menu_data, current_menu_item);

    // The top level menu we want to build is the first entry in the trail.
    const menu = buildMenu(trail[0], getMenuTrailIDs(trail));

    return menu;
}

SidebarMenu.propTypes = {
    url: PropTypes.string,
    menus: PropTypes.array,
}

SidebarMenu.defaultProps = {
    url: null,
    menus: ['main', 'footer'],
}

export default SidebarMenu